export const PEAK_RESOLUTION = 480;
export const MIN_BAR = 0.08;
const SILENT_BAR = 0.12;
const SPEECH_BAR = 0.72;

export const computePeaks = (channels: readonly Float32Array[], bars: number = PEAK_RESOLUTION): number[] => {
    const length = channels.reduce((longest, channel) => Math.max(longest, channel.length), 0);
    if (bars <= 0 || length === 0) return [];
    const raw: number[] = [];
    for (let bar = 0; bar < bars; bar++) {
        const from = Math.floor((bar * length) / bars);
        const to = Math.max(from + 1, Math.floor(((bar + 1) * length) / bars));
        let peak = 0;
        for (const channel of channels) {
            for (let i = from; i < to && i < channel.length; i++) {
                const value = Math.abs(channel[i]);
                if (value > peak) peak = value;
            }
        }
        raw.push(peak);
    }
    const loudest = raw.reduce((max, value) => Math.max(max, value), 0);
    if (loudest === 0) return raw.map(() => MIN_BAR);
    return raw.map((value) => Math.max(MIN_BAR, value / loudest));
};

export const resamplePeaks = (peaks: readonly number[], bars: number): number[] => {
    if (bars <= 0 || peaks.length === 0) return [];
    if (peaks.length === bars) return [...peaks];
    return Array.from({length: bars}, (_, bar) => {
        const from = Math.floor((bar * peaks.length) / bars);
        const to = Math.max(from + 1, Math.floor(((bar + 1) * peaks.length) / bars));
        let peak = 0;
        for (let i = from; i < to && i < peaks.length; i++) {
            peak = Math.max(peak, peaks[i]);
        }
        return peak;
    });
};

export const speechEnvelope = (
    intervals: readonly {startTime: number; endTime: number}[],
    duration: number,
    bars: number,
): number[] => {
    if (bars <= 0) return [];
    if (!(duration > 0)) return Array.from({length: bars}, () => SILENT_BAR);
    const width = duration / bars;
    return Array.from({length: bars}, (_, bar) => {
        const from = bar * width;
        const to = from + width;
        const covered = intervals.reduce((sum, interval) =>
            sum + Math.max(0, Math.min(to, interval.endTime) - Math.max(from, interval.startTime)), 0);
        const coverage = Math.min(1, covered / width);
        return SILENT_BAR + (SPEECH_BAR - SILENT_BAR) * coverage;
    });
};
